import React from 'react';
import SectionHeader from './SectionHeader';
import CurrencyInput from './CurrencyInput';
import NotesInput from './NotesInput';
import AutoCheckbox from './AutoCheckbox';

/**
 * Section 5 — Medical
 *
 * Important: do NOT include insurance premiums automatically deducted from
 * wages — unless the client opts in via the AUTO checkbox.
 */
export default function StepMedical({ data = {}, onChange }) {
  function set(field, value) {
    onChange({ ...data, [field]: value });
  }

  const groups = [
    {
      costKey: 'dental_copays',
      costLabel: 'Dental Copays & Expenses',
      insKey: 'dental_insurance',
      insLabel: 'Dental Insurance',
      autoKey: 'dental_insurance_auto',
    },
    {
      costKey: 'medical_copays',
      costLabel: 'Medical Copays & Expenses',
      insKey: 'medical_insurance',
      insLabel: 'Medical Insurance',
      autoKey: 'medical_insurance_auto',
    },
    {
      costKey: 'vision_copays',
      costLabel: 'Vision Copays & Expenses (glasses, contacts)',
      insKey: 'vision_insurance',
      insLabel: 'Vision Insurance',
      autoKey: 'vision_insurance_auto',
    },
    {
      costKey: 'pharmacy_medications',
      costLabel: 'Pharmacy & Medications',
      insKey: 'prescription_insurance',
      insLabel: 'Prescription Insurance / Medicare Part D',
      autoKey: 'prescription_insurance_auto',
    },
  ];

  const total =
    groups.reduce(
      (sum, g) =>
        sum + (Number(data[g.costKey]) || 0) + (Number(data[g.insKey]) || 0),
      0
    ) + (Number(data.alternative_health_care) || 0);

  return (
    <div>
      <SectionHeader
        number={5}
        title="Medical"
        tip="Focus on costs you pay yourself — including from an HSA or FSA.
        We also want to capture premiums deducted from your paycheck - you can calculate the annual total or check the AUTO box to have our team review your payslip for figures."
      />

      {groups.map(({ costKey, costLabel, insKey, insLabel, autoKey }) => (
        <div key={costKey} className="mb-3 pb-2 border-bottom">
          <CurrencyInput
            id={costKey}
            label={costLabel}
            value={data[costKey] ?? null}
            onChange={(val) => set(costKey, val)}
          />
          <CurrencyInput
            id={insKey}
            label={insLabel}
            value={data[insKey] ?? null}
            onChange={(val) => set(insKey, val)}
          />
          <AutoCheckbox
            id={`${autoKey}_check`}
            checked={!!data[autoKey]}
            onChange={(val) => set(autoKey, val)}
          />
        </div>
      ))}

      <CurrencyInput
        id="alternative_health_care"
        label="Alternative / Other Health Care"
        value={data.alternative_health_care ?? null}
        onChange={(val) => set('alternative_health_care', val)}
      />

      {total > 0 && (
        <div className="cashflow-total-box mt-3">
          <span className="fw-semibold">5: Total Medical Expenses</span>
          <span className="cashflow-total-amount">
            ${total.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </span>
        </div>
      )}

      <NotesInput value={data.notes} onChange={(val) => set('notes', val)} />
    </div>
  );
}
